"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useId, useState, type FormEvent } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PasswordInput } from "./PasswordInput"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import type { AuthMode } from "./authMode"

interface ResetPasswordFormProps {
  email: string
  onModeChange: (mode: AuthMode, email?: string) => void
}

export function ResetPasswordForm({ email, onModeChange }: ResetPasswordFormProps) {
  const { t } = useI18n()
  const { confirmForgotPassword, errorKey, clearError } = useAuth()
  const [code, setCode] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const codeId = useId()
  const passwordId = useId()

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    clearError()
    setSubmitting(true)
    try {
      await confirmForgotPassword(email, code, newPassword)
      setDone(true)
    } catch {
      // errorKey is already set by the auth context.
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="flex flex-col gap-4">
        <p role="status" className="text-sm text-green-2">
          {t("auth.resetSuccess")}
        </p>
        <Button type="button" className="min-h-11" onClick={() => onModeChange("signIn", email)}>
          {t("auth.backToSignIn")}
        </Button>
      </div>
    )
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
      <p className="text-sm text-ink-2">{t("auth.resetIntro", { email })}</p>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={codeId} className="text-sm font-medium text-ink">
          {t("auth.resetCodeLabel")}
        </label>
        <Input
          id={codeId}
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={event => setCode(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={passwordId} className="text-sm font-medium text-ink">
          {t("auth.newPasswordLabel")}
        </label>
        <PasswordInput
          id={passwordId}
          name="new-password"
          autoComplete="new-password"
          minLength={8}
          required
          value={newPassword}
          onChange={event => setNewPassword(event.target.value)}
        />
      </div>

      {errorKey && (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      )}

      <Button type="submit" disabled={submitting} className="min-h-11">
        {t("auth.resetButton")}
      </Button>

      <button
        type="button"
        className="text-center text-sm text-muted-foreground underline underline-offset-2"
        onClick={() => onModeChange("signIn")}
      >
        {t("auth.backToSignIn")}
      </button>
    </form>
  )
}
